import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { AIInsightBanner } from "../components/AIInsightBanner";
import { PageTimeline } from "../components/PageTimeline";
import { KpiCard } from "../components/KpiCard";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { fmtINR, fmtNum } from "../lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";

export default function SalesIntelligence() {
  const { asOf, period, projectsQS } = usePeriod();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!asOf) return;
    api.get(`/kpi/executive?as_of=${asOf}&period=${period}${projectsQS}`).then((r) => setData(r.data));
  }, [asOf, period, projectsQS]);

  if (!data) return <><Header title="Sales Intelligence" /><div className="p-6 text-sm text-slate-500">Loading...</div></>;

  const k = data.kpis;
  const projects = data.project_sales || [];
  const top = projects.slice(0, 5);
  const bottom = [...projects].sort((a, b) => a.net - b.net).slice(0, 5);

  return (
    <>
      <Header title="Sales Intelligence" subtitle="Performance · Net Flow · Outliers" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <PageTimeline fyLabel={data.fy_label} />
        <AIInsightBanner context={{ period: data.period, fy_label: data.fy_label, kpis: k, top_projects: top }} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Gross Sales" value={fmtINR(k.gross_sales, { short: true })} delta={data.delta?.gross_sales} />
          <KpiCard label="Cancellations" value={fmtINR(k.cancel_value, { short: true })} delta={data.delta?.cancel_value} />
          <KpiCard label="Net Sales" value={fmtINR(k.net_sales, { short: true })} delta={data.delta?.net_sales} accent />
          <KpiCard label="Unit Count" value={fmtNum(k.sales_count)} dark />
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-4">
          <div className="text-sm font-semibold mb-3">Project-wise Gross → Cancel → Net</div>
          <ResponsiveContainer width="100%" height={Math.max(300, projects.length * 30)}>
            <BarChart data={projects} layout="vertical" margin={{ left: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="project_name" width={140} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
              <Bar dataKey="gross" stackId="a" fill="#0F1D32" name="Gross Sales" />
              <Bar dataKey="cancel" stackId="b" fill="#991B1B" name="Cancellations" />
              <Bar dataKey="net" stackId="c" fill="#D4AF37" name="Net Sales" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">Top Performers</div>
            <table className="w-full text-xs" data-testid="top-performers-table">
              <tbody>
                {top.map((p, i) => (
                  <tr key={p.project_name} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-400 font-mono w-6">{i + 1}</td>
                    <td className="px-4 py-2 truncate">{p.project_name}</td>
                    <td className="numeric-cell px-4 py-2 font-semibold">{fmtINR(p.net, { short: true })}</td>
                    <td className="numeric-cell px-4 py-2 text-slate-500">{p.count} units</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white rounded-md border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">Bottom Performers</div>
            <table className="w-full text-xs" data-testid="bottom-performers-table">
              <tbody>
                {bottom.map((p, i) => (
                  <tr key={p.project_name} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-400 font-mono w-6">{i + 1}</td>
                    <td className="px-4 py-2 truncate">{p.project_name}</td>
                    <td className="numeric-cell px-4 py-2 font-semibold">{fmtINR(p.net, { short: true })}</td>
                    <td className="numeric-cell px-4 py-2 text-slate-500">{p.count} units</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-4">
          <div className="text-sm font-semibold mb-3">Monthly Trend · {data.fy_label}</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
              <Bar dataKey="net_sales" fill="#0F1D32" radius={[4, 4, 0, 0]} name="Net Sales" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}
